import type { CSSProperties } from 'react';

/** The tokens a figure can be counted in, with their logo and the colour of their coin's rim. */
export type Token = 'SYN' | 'sSYN' | 'USDT' | 'BNB' | 'LP';
const TOKENS: Record<Token, { src: string; rim: string; dark: string }> = {
  SYN: { src: '/token-syntra.png', rim: '#8b5cf6', dark: '#3b1580' },
  sSYN: { src: '/token-ssyntra.png', rim: '#e879f9', dark: '#6d1b7e' },
  USDT: { src: '/token-usdt.svg', rim: '#34c79a', dark: '#0f5340' },
  BNB: { src: '/token-bnb.svg', rim: '#f6c445', dark: '#8a6410' },
  LP: { src: '/token-lp.svg', rim: '#a78bfa', dark: '#3b1580' },
};
const rimVars = (t: Token) => ({ '--rim': TOKENS[t].rim, '--rim-dark': TOKENS[t].dark });

/**
 * A 3D coin with the token's logo on both faces and a solid rim, standing next to an amount or a price of that
 * token. `spin` turns it every few seconds; `hover` only while the row or link it sits in is hovered.
 */
export function Coin({ token, size = 32, motion = 'spin' }: { token: Token; size?: number; motion?: 'spin' | 'hover' | 'still' }) {
  const half = Math.max(1.5, size * 0.07); // half the coin's thickness
  const rim: number[] = [];
  for (let z = -half + 0.5; z < half; z += 0.75) rim.push(z);
  const { src } = TOKENS[token];
  return (
    <span className={`coin3d ${motion}`} style={{ width: size, height: size, ...rimVars(token) } as CSSProperties} aria-hidden="true" data-token={token}>
      <span className="coin3d-body">
        {rim.map((z) => (
          <i key={z} style={{ transform: `translateZ(${z.toFixed(2)}px)` }} />
        ))}
        <img src={src} alt="" draggable={false} style={{ transform: `translateZ(${half.toFixed(2)}px)` }} />
        <img src={src} alt="" draggable={false} style={{ transform: `rotateY(180deg) translateZ(${half.toFixed(2)}px)` }} />
      </span>
    </span>
  );
}

/** How many coins a stack shows for `value` when `max` (the largest value it is compared with) gets `most`. */
export function coinCount(value: number, max: number, most = 8): number {
  if (!Number.isFinite(value) || !Number.isFinite(max) || max <= 0 || value <= 0) return 0;
  return Math.max(1, Math.min(most, Math.round((value / max) * most)));
}

/**
 * A stack of coins lying flat, as tall as the value it stands for compared with its neighbours: a plan's APR next
 * to the other plans', or one side of a pool next to the other. The coins drop in one after another.
 */
export function CoinStack({ token, count, size = 26, label }: { token: Token; count: number; size?: number; label: string }) {
  const n = Math.max(0, Math.round(count));
  return (
    <span className="coin-stack" style={{ '--s': `${size}px`, '--n': n, ...rimVars(token) } as CSSProperties} role="img" aria-label={label} data-coins={n}>
      {Array.from({ length: n }, (_, k) => (
        <i key={k} style={{ '--k': k, backgroundImage: `url(${TOKENS[token].src})` } as CSSProperties} />
      ))}
    </span>
  );
}

/** The two sides of a pool as coin stacks scaled by their dollar value: a balanced pool stands level. */
export function PoolStacks({ sides, size = 26 }: { sides: { token: Token; usd: number; label: string }[]; size?: number }) {
  const max = Math.max(...sides.map((s) => (Number.isFinite(s.usd) ? s.usd : 0)));
  return (
    <div className="pool-stacks" data-testid="pool-stacks">
      {sides.map((s) => (
        <div key={s.token} className="pool-side">
          <CoinStack token={s.token} count={coinCount(s.usd, max, 7)} size={size} label={s.label} />
          <span className="num">{s.label}</span>
        </div>
      ))}
    </div>
  );
}
